import LocationOrangeIcon from 'assets/svg/location/location-orange.svg'
import LegacySupplierCard from 'common/components/Card/SupplierCard'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import { mediaflexCdn, pathToCdn } from 'lib/cdn'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ISupplierItem } from 'services/Marketplace/models/RecommendationsModel/interface'
import { useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const defaultProductImage = pathToCdn('/assets/img/default-img.svg')

export type SellerRecommendationVariant =
  | 'modern'
  | 'classic'
  | 'both'

interface SellerRecommendationProps {
  variant?: SellerRecommendationVariant
}

const GradientWrapper = styled.section`
  width: 100%;
  padding: 48px 0;
  background: linear-gradient(
    97deg,
    rgba(240, 90, 34, 0.1) 3.5%,
    rgba(174, 43, 55, 0.1) 99.45%
  );
`

const SupplierSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`

const SupplierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Subtitle = styled(Typography)`
  color: ${(props) => props.theme.colors.gray};
  max-width: 640px;
`

const SupplierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`

const SupplierLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.orange};
    outline-offset: 4px;
  }
`

const ModernCard = styled.article`
  border-radius: 24px;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  }
`

const Banner = styled.div<{ image?: string }>`
  height: 150px;
  background: ${(props) => {
    const imageUrl = props.image
      ? mediaflexCdn(`/f=auto,w=500/${props.image}`)
      : mediaflexCdn('f=auto,w=500/assets/img/default-img.svg')
    return `center/cover url(${imageUrl})`
  }};
`

const BannerWrapper = styled.div`
  position: relative;
`

const LogoBadge = styled.div<{ logo?: string }>`
  box-sizing: border-box;
  width: 100px;
  height: 100px;
  background: ${(props) => {
    const imageUrl = props.logo
      ? mediaflexCdn(`/f=auto,w=100/${props.logo}`)
      : mediaflexCdn('f=auto,w=100/assets/img/default-img.svg')
    return `center/cover url(${imageUrl})`
  }};
  border: 4px solid ${(props) => props.theme.colors.white};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  position: absolute;
  left: 50%;
  bottom: -36px;
  transform: translate(-50%, 0);
`

const Info = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
  padding-top: 50px;
`

const Description = styled(Typography)`
  color: ${(props) => props.theme.colors.gray};
`

const LocationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.colors.gray};
  font-size: 14px;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Chip = styled.span`
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.colors.grayLighter};
  font-size: 12px;
  color: ${(props) => props.theme.colors.grayDark};
  text-transform: uppercase;
`

const VerifiedChip = styled(Chip)`
  color: ${(props) => props.theme.colors.green};
  border-color: ${(props) => props.theme.colors.green};
`

const ProductPreview = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 20px 18px;
`

const ProductImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid ${(props) => props.theme.colors.grayLighter};
`

const isExternalLink = (href: string): boolean =>
  href?.startsWith('http') ?? false

const buildSupplierHref = (supplier: Record<string, any>): string => {
  if (!supplier) {
    return WEB_DESKTOP_RALALI_URL
  }

  const { redirectUrl, vendorAlias } = supplier

  if (redirectUrl) {
    if (redirectUrl.startsWith('http')) {
      return redirectUrl
    }
    const sanitizedPath = redirectUrl.startsWith('/')
      ? redirectUrl
      : `/${redirectUrl}`
    return `${WEB_DESKTOP_RALALI_URL}${sanitizedPath}`
  }

  if (vendorAlias) {
    return `${WEB_DESKTOP_RALALI_URL}/v/${vendorAlias}`
  }

  return WEB_DESKTOP_RALALI_URL
}

const getSupplierKey = (
  supplier: Record<string, any>,
  fallback: string,
): string => {
  const vendorId = supplier?.vendorId
  return (
    supplier?.vendorAlias ||
    supplier?.sellerName ||
    (vendorId !== undefined && vendorId !== null
      ? String(vendorId)
      : undefined) ||
    supplier?.redirectUrl ||
    fallback
  )
}

function SellerRecommendation({
  variant = 'classic',
}: SellerRecommendationProps): JSX.Element {
  const { t } = useTranslation('home')
  const { data: recommendationsData } = useStoreState(
    (states) => states.recommendations,
  )
  const [suppliers, setSuppliers] = useState([])

  useEffect(() => {
    if (recommendationsData?.suppliers) {
      setSuppliers(recommendationsData.suppliers)
    }
  }, [recommendationsData])

  const normalizeCategories = (category) => {
    if (Array.isArray(category)) return category
    if (category) return [category]
    return []
  }

  const shouldRenderModern = variant !== 'classic'
  const shouldRenderClassic = variant !== 'modern'

  const renderModernCard = (
    supplier: Record<string, any>,
    safeCategories: string[],
    incomingItems: ISupplierItem[],
    key: string,
  ) => {
    const supplierLink = buildSupplierHref(supplier)
    const external = isExternalLink(supplierLink)
    return (
      <SupplierLink
        key={key}
        href={supplierLink}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}
        aria-label={
          supplier.sellerName
            ? t('SELLER_VISIT_STORE_ARIA', {
                name: supplier.sellerName,
              })
            : t('SELLER_VISIT_PARTNER_STORE_ARIA')
        }
      >
        <ModernCard>
          <BannerWrapper>
            <Banner image={supplier.imageUrl} />
            <LogoBadge logo={supplier.logoUrl || supplier.imageUrl} />
          </BannerWrapper>
          <Info>
            <Typography type="bh4">{supplier.sellerName}</Typography>
            <LocationRow>
              <LocationOrangeIcon width="15" height="15" />
              <Description type="rparagraph">
                {supplier.sellerCity || supplier.location}
              </Description>
            </LocationRow>
            <MetaRow>
              <VerifiedChip>{t('SELLER_CERTIFIED')}</VerifiedChip>
              {supplier.certification && (
                <Chip>{supplier.certification}</Chip>
              )}
              {safeCategories.slice(0, 2).map((category) => (
                <Chip key={category}>{category}</Chip>
              ))}
            </MetaRow>
          </Info>
          <ProductPreview>
            {incomingItems.slice(0, 3).map((item) => {
              const imageUrl = item.imageUrl || ''
              const src = imageUrl
                ? mediaflexCdn(`/f=auto,w=100/${imageUrl}`)
                : defaultProductImage
              return (
                <ProductImage
                  key={item.itemId || item.itemName}
                  src={src}
                  alt={item.itemName}
                  onError={({ currentTarget }) => {
                    if (currentTarget.src !== defaultProductImage) {
                      currentTarget.src = defaultProductImage
                    }
                  }}
                />
              )
            })}
          </ProductPreview>
        </ModernCard>
      </SupplierLink>
    )
  }

  const renderClassicCard = (
    supplier: Record<string, any>,
    safeCategories: string[],
    incomingItems: ISupplierItem[],
    key: string,
  ) => (
    <LegacySupplierCard
      key={key}
      alias={supplier.redirectUrl}
      categories={safeCategories}
      supplierImage={supplier.imageUrl}
      items={incomingItems}
      supplierName={supplier.sellerName}
      location={supplier.sellerCity || supplier.location}
    />
  )

  const cards = suppliers.reduce<JSX.Element[]>(
    (acc, supplier, index) => {
      const supplierCategories = normalizeCategories(
        supplier?.sellerCategory,
      )
      const safeCategories = Array.isArray(supplierCategories)
        ? supplierCategories
        : []
      const incomingItems: ISupplierItem[] = Array.isArray(
        supplier?.items,
      )
        ? supplier.items
        : []
      const supplierKey = getSupplierKey(
        supplier,
        `supplier-${index}`,
      )

      if (shouldRenderModern) {
        acc.push(
          renderModernCard(
            supplier,
            safeCategories,
            incomingItems,
            `${supplierKey}-modern`,
          ),
        )
      }

      if (shouldRenderClassic) {
        acc.push(
          renderClassicCard(
            supplier,
            safeCategories,
            incomingItems,
            `${supplierKey}-classic`,
          ),
        )
      }

      return acc
    },
    [],
  )

  return (
    <GradientWrapper>
      <SupplierSection>
        <SupplierHeader>
          <div>
            <Typography
              type="rlabel"
              letterSpacing="1px"
              marginBottom="6px"
            >
              {t('PARTNER_RALALI', { ns: 'home' })}
            </Typography>
            <Typography type="bh2" marginBottom="6px">
              {t('RECOMMENDATED_SUPPLIERS', { ns: 'home' })}
            </Typography>
            <Subtitle type="p">
              {t('TRUSTED_SUPPLIERS_DESCRIPTION', { ns: 'home' })}
            </Subtitle>
          </div>
        </SupplierHeader>
        <SupplierGrid>{cards}</SupplierGrid>
      </SupplierSection>
    </GradientWrapper>
  )
}

export default SellerRecommendation
